/**
 * Orders data hooks — PEL-823.
 *
 * TanStack Query wrappers over the orders module of
 * `@pelipost/consumer-api-client`. Screens never call `api.orders.*`
 * directly — they use these hooks so cache invalidation stays consistent.
 *
 * Cache keys:
 *   ['orders']                       — base
 *   ['orders', 'list', page]         — paginated list
 *   ['orders', 'detail', id]         — one order
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { Order, OrderDetail } from '@pelipost/consumer-api-client';
import { api } from '../lib/api/client';

export const ordersKeys = {
  all: ['orders'] as const,
  list: (page: number) => ['orders', 'list', page] as const,
  detail: (id: string) => ['orders', 'detail', id] as const,
};

/** List orders (paginated). Defaults to page 1. */
export function useOrdersList(
  page: number = 1,
  options?: Omit<
    UseQueryOptions<{ orders: Order[]; total: number }>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<{ orders: Order[]; total: number }>({
    queryKey: ordersKeys.list(page),
    queryFn: () => api.orders.getOrders(page),
    ...options,
  });
}

/** Fetch a single order by id. Disabled when id is empty. */
export function useOrder(id: string | undefined) {
  return useQuery<OrderDetail>({
    queryKey: id ? ordersKeys.detail(id) : ['orders', 'disabled'],
    queryFn: () => api.orders.getOrder(id as string),
    enabled: Boolean(id),
  });
}

/** Re-order a past order — creates a new cart. */
export function useReorder() {
  const qc = useQueryClient();
  return useMutation<{ cartId: string }, Error, string>({
    mutationFn: (orderId) => api.orders.reorder(orderId),
    onSuccess: () => {
      // Cart list / cart contents invalidation happens in the cart hooks;
      // here we just ensure the order list is fresh in case status shifts.
      qc.invalidateQueries({ queryKey: ordersKeys.all });
    },
  });
}
